/* eslint-disable */
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useProgressTracking } from '../hooks/useProgressTracking';
import { useJournalData } from '../hooks/useJournalData';
import '../styles/CalendarModal.css';

const CalendarModal = ({ 
  isOpen, 
  onClose, 
  user, 
  userGoal, 
  businessPlan,
  selectedDate,
  onDateSelect,
  // Journal-related props
  openJournal,
  createNewEntry,
  deleteJournal
}) => {
  const [currentView, setCurrentView] = useState('quarter'); // quarter, week, day, journal
  const [selectedQuarter, setSelectedQuarter] = useState(1);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [openMonth, setOpenMonth] = useState(null);
  const [currentDate] = useState(new Date());
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showEntryModal, setShowEntryModal] = useState(false);
  
  const { 
    hierarchicalPlan, 
    progressData, 
    getCurrentPeriodInfo,
    loading 
  } = useProgressTracking(user, userGoal, businessPlan);

  // Use journal data hook
  const {
    savedJournals,
    getAllEntriesForMonth,
    getEntriesForMonthByDate
  } = useJournalData(user);

  const currentPeriod = getCurrentPeriodInfo();

  useEffect(() => {
    if (isOpen && currentPeriod && currentPeriod.quarter && currentPeriod.week) {
      setSelectedQuarter(currentPeriod.quarter);
      setSelectedWeek(currentPeriod.week);
    }
  }, [isOpen, currentPeriod]);

  const openEntryModal = (entry) => {
    setSelectedEntry(entry);
    setShowEntryModal(true);
  };

  const closeEntryModal = () => {
    setSelectedEntry(null);
    setShowEntryModal(false);
  };

  const getEntryIcon = (entry) => {
    if (entry.type === 'structured') {
      return entry.primaryType === 'daily' ? '☀️' : 
             entry.primaryType === 'meeting' ? '🤝' : 
             entry.primaryType === 'project' ? '🚀' : '📋';
    }
    return '📝';
  };

  const getEntryTitle = (entry) => {
    if (entry.type === 'structured') {
      return entry.title || `${entry.primaryType} Entry`;
    }
    return entry.title || 'Journal Entry';
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderQuarterView = () => {
    if (!hierarchicalPlan || !hierarchicalPlan.quarters) return <div>Loading plan...</div>;
    
    return (
      <div className="calendar-quarters">
        <div className="quarters-grid">
          {hierarchicalPlan.quarters.map((quarter, index) => (
            <div 
              key={quarter.id} 
              className={`quarter-card ${currentPeriod?.quarter === quarter.id ? 'current' : ''}`}
                onClick={() => {
                setSelectedQuarter(quarter.id);
                setCurrentView('week');
              }}
            >
              <div className="quarter-header">
                <h3>Q{quarter.id}</h3>
                <span className="quarter-days">Days {quarter.startDay}-{quarter.endDay}</span>
              </div>
              <div className="quarter-title">{quarter.title}</div>
              <div className="quarter-description">{quarter.description}</div>
              <div className="quarter-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ 
                      width: `${currentPeriod?.quarter === quarter.id ? (currentPeriod?.quarterProgress || 0) : 0}%` 
                    }}
                  />
                  </div>
                <span className="progress-text">
                  {currentPeriod?.quarter === quarter.id ? Math.round(currentPeriod?.quarterProgress || 0) : 0}%
                </span>
              </div>
        </div>
          ))}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    if (!hierarchicalPlan) return <div>Loading plan...</div>;

    const selectedQuarterData = hierarchicalPlan.quarters[selectedQuarter - 1];
    if (!selectedQuarterData) return <div>Quarter not found</div>;

    return (
      <div className="calendar-weeks">
        <div className="view-header">
            <button 
            className="back-btn" 
            onClick={() => setCurrentView('quarter')}
            >
            ← Back to Quarters
            </button>
          <h3>{selectedQuarterData.title} - Weeks</h3>
          </div>
        <div className="weeks-grid">
          {selectedQuarterData.weeks.map((week, index) => (
            <div 
              key={week.id} 
              className={`week-card ${
                currentPeriod?.quarter === selectedQuarter && currentPeriod?.week === week.id ? 'current' : ''
              }`}
              onClick={() => {
                setSelectedWeek(week.id);
                setCurrentView('day');
              }}
            >
              <div className="week-header">
                <h4>Week {week.id}</h4>
                <span className="week-days">Days {week.startDay}-{week.endDay}</span>
                          </div>
              <div className="week-focus">{week.focus}</div>
              <div className="week-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ 
                      width: `${
                        currentPeriod?.quarter === selectedQuarter && currentPeriod?.week === week.id 
                          ? (progressData?.weeklyProgress || 0) 
                          : 0
                      }%` 
                    }}
                  />
                      </div>
                <span className="progress-text">
                  {currentPeriod?.quarter === selectedQuarter && currentPeriod?.week === week.id 
                    ? Math.round(progressData?.weeklyProgress || 0) 
                    : 0}%
                          </span>
                      </div>
                    </div>
                  ))}
                </div>
            </div>
    );
  };

  const renderDayView = () => {
    if (!hierarchicalPlan) return <div>Loading plan...</div>;

    const selectedQuarterData = hierarchicalPlan.quarters[selectedQuarter - 1];
    const selectedWeekData = selectedQuarterData?.weeks[selectedWeek - 1];
    
    if (!selectedWeekData) return <div>Week not found</div>;

    return (
      <div className="calendar-days">
        <div className="view-header">
                <button 
            className="back-btn" 
            onClick={() => setCurrentView('week')}
                >
            ← Back to Weeks
                </button>
          <h3>Week {selectedWeek} - Daily Actions</h3>
        </div>
        <div className="days-grid">
          {selectedWeekData.days.map((day, index) => {
            const isToday = day.globalDay === (progressData?.daysSinceStart || 1);
            const dayOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][index % 7];
            
            return (
              <div 
                key={day.id} 
                className={`day-card ${isToday ? 'today' : ''} ${day.globalDay <= (progressData?.daysSinceStart || 1) ? 'past' : 'future'}`}
              >
                <div className="day-header">
                  <h4>{dayOfWeek}</h4>
                  <span className="day-number">Day {day.globalDay}</span>
                  <span className="day-date">{new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              </div>
                <div className="day-actions">
                  <div className="action-text">
                    <div className="primary-action">{day.actions?.primary || 'No primary action set'}</div>
                    {day.actions?.secondary && day.actions.secondary.length > 0 && (
                      <div className="secondary-actions">
                        {day.actions.secondary.map((action, idx) => (
                          <div key={idx} className="secondary-action">• {action}</div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="action-meta">
                    <span className="priority">{day.priority} Priority</span>
                    <span className="hours">{day.estimatedHours || day.actions?.timeCommitment || '4-6h'}</span>
            </div>
          </div>
                {isToday && <div className="today-badge">TODAY</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderJournalView = () => {
    const monthsArray = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const getCurrentMonth = () => {
      return currentDate.getMonth();
    };

    const getJournalsForMonth = (monthIndex) => {
      if (typeof savedJournals === 'function') {
        return savedJournals(monthIndex) || [];
      }
      return getAllEntriesForMonth ? getAllEntriesForMonth(monthIndex, currentDate) || [] : [];
    };

    const getJournalColor = (entry, index) => {
      if (entry.type === 'structured') {
        switch (entry.primaryType) {
          case 'daily': return '#007AFF';
          case 'meeting': return '#34C759'; 
          case 'project': return '#AF52DE';
          default: return '#8E8E93';
        }
      }
      
      const colors = [
        '#FF3B30', '#FF9500', '#FFCC02', '#34C759', '#00C7BE',
        '#007AFF', '#5856D6', '#AF52DE', '#FF2D92', '#A2845E'
      ];
      return colors[index % colors.length];
    };

    const toggleMonth = (index) => {
      const entriesForMonth = getJournalsForMonth(index);
      
      if (entriesForMonth.length > 0) {
        const newOpenMonth = openMonth === index ? null : index;
        setOpenMonth(newOpenMonth);
      }
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      if (date.toDateString() === today.toDateString()) {
        return 'Today';
      } else if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
      } else {
        return date.toLocaleDateString('en-US', { 
          weekday: 'long', 
          month: 'short', 
          day: 'numeric' 
        });
      }
    };



    return (
      <div className="calendar-journal">
        <div className="view-header">
          <h3>Journal Entries by Month</h3>
        </div>
        
        {/* Month Ribbon */}
        <div className="month-ribbon">
          {monthsArray.map((month, index) => {
            const entriesForMonth = getJournalsForMonth(index);
            const isCurrentMonth = index === getCurrentMonth();
            const isSelected = openMonth === index;
            const hasEntries = entriesForMonth.length > 0;

            return (
              <div
                key={month}
                className={`month-item ${isCurrentMonth ? 'current' : ''} ${isSelected ? 'selected' : ''} ${hasEntries ? 'has-entries' : ''}`}
                onClick={() => toggleMonth(index)}
                style={{ cursor: hasEntries ? 'pointer' : 'default' }}
              >
                <div className="month-header">
                  <span className="month-name">{month}</span>
                  {hasEntries && (
                    <span className="entry-count">{entriesForMonth.length}</span>
                  )}
                </div>

                {hasEntries && (
                  <div className="entry-indicators">
                    {entriesForMonth.slice(0, 4).map((entry, entryIndex) => (
                      <div
                        key={entryIndex}
                        className="entry-dot"
                        style={{ backgroundColor: getJournalColor(entry, entryIndex) }}
                      />
                    ))}
                    {entriesForMonth.length > 4 && (
                      <span className="more-entries">+{entriesForMonth.length - 4}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Expandable Drawer */}
        <div className={`month-drawer ${openMonth !== null ? 'open' : ''}`}>
          {openMonth !== null && (
            <div className="drawer-content">
              <div className="drawer-header">
                <div className="drawer-title">
                  <h3>{monthsArray[openMonth]} {currentDate ? currentDate.getFullYear() : new Date().getFullYear()}</h3>
                  <span className="entry-count-drawer">
                    {getJournalsForMonth(openMonth)?.length || 0} entries
                  </span>
                </div>
                <div className="drawer-actions">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      if (createNewEntry) createNewEntry();
                    }}
                    className="new-entry-btn"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14">
                      <path 
                        d="M7 1V13M1 7H13" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round"
                      />
                    </svg>
                    New Entry
                  </button>
                  <button 
                    onClick={() => setOpenMonth(null)}
                    className="close-btn"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14">
                      <path 
                        d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" 
                        stroke="currentColor" 
                        strokeWidth="1.5" 
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="entries-by-date">
                {getEntriesForMonthByDate && getEntriesForMonthByDate(openMonth, currentDate) && Object.keys(getEntriesForMonthByDate(openMonth, currentDate)).length > 0 ? (
                  Object.entries(getEntriesForMonthByDate(openMonth, currentDate))
                    .sort(([a], [b]) => new Date(b) - new Date(a))
                    .map(([date, entriesForDate]) => (
                      <div key={date} className="date-group">
                        <div className="date-header">
                          <span className="date-label">{formatDate(date)}</span>
                          <span className="date-secondary">
                            {new Date(date).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </span>
                        </div>
                        
                        <div className="entries-list">
                          {entriesForDate.map((entry) => (
                            <div
                              key={entry.id}
                              className={`entry-item ${entry.type === 'structured' ? 'structured' : ''}`}
                              data-type={entry.primaryType}
                              onClick={(e) => {
                                e.stopPropagation();
                                openEntryModal(entry);
                              }}
                            >
                              <div 
                                className="entry-indicator"
                                style={{ backgroundColor: getJournalColor(entry, 0) }}
                              ></div>
                              <div className="entry-content">
                                <div className="entry-title">{getEntryTitle(entry)}</div>
                                <div className="entry-meta">
                                  <span className="entry-time">
                                    {new Date(entry.createdAt).toLocaleTimeString('en-US', { 
                                      hour: 'numeric', 
                                      minute: '2-digit',
                                      hour12: true 
                                    })}
                                  </span>
                                  {entry.type === 'structured' && (
                                    <span className="entry-type">{entry.primaryType}</span>
                                  )}
                                </div>
                                {entry.content && (
                                  <div className="entry-preview">
                                    {entry.content.substring(0, 80)}
                                    {entry.content.length > 80 && '...'}
                                  </div>
                                )}
                              </div>
                              <button
                                className="delete-entry-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (deleteJournal) deleteJournal(entry.id, e);
                                }}
                                title="Delete entry"
                              >
                                <svg width="12" height="12" viewBox="0 0 12 12">
                                  <path 
                                    d="M9 3L3 9M3 3L9 9" 
                                    stroke="currentColor" 
                                    strokeWidth="1.5" 
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                ) : (
                  <div className="no-entries">
                    <p>No entries for {monthsArray[openMonth]}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return ReactDOM.createPortal(
    <div className={`calendar-modal-overlay ${isOpen ? 'open' : ''}`} onClick={handleOverlayClick}>
      <div className="calendar-modal">
        <div className="calendar-modal-header">
          <div className="modal-title">
            <h2>{hierarchicalPlan?.centralGoal?.title || 'Project Timeline'}</h2>
            <div className="current-status">
              Day {progressData?.daysSinceStart || 1} • Quarter {currentPeriod?.quarter || 1} • Week {currentPeriod?.week || 1}
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
          </div>
          
        <div className="calendar-modal-nav">
              <button 
            className={`nav-btn ${currentView === 'quarter' ? 'active' : ''}`}
            onClick={() => setCurrentView('quarter')}
              >
            Quarters
              </button>
              <button 
            className={`nav-btn ${currentView === 'week' ? 'active' : ''}`}
            onClick={() => setCurrentView('week')}
            disabled={!selectedQuarter}
              >
            Weeks
              </button>
              <button 
            className={`nav-btn ${currentView === 'day' ? 'active' : ''}`}
            onClick={() => setCurrentView('day')}
            disabled={!selectedWeek}
              >
            Days
              </button>
              <button 
            className={`nav-btn ${currentView === 'journal' ? 'active' : ''}`}
            onClick={() => setCurrentView('journal')}
              >
            Journal
              </button>
            </div>
            
        <div className="calendar-modal-content">
            {loading && currentView !== 'journal' ? (
            <div className="loading-state">Loading your hierarchical plan...</div>
          ) : (
            <>
              {currentView === 'quarter' && renderQuarterView()}
              {currentView === 'week' && renderWeekView()}
              {currentView === 'day' && renderDayView()}
              {currentView === 'journal' && renderJournalView()}
            </>
          )}
        </div>
        
        <div className="calendar-modal-footer">
          <div className="progress-summary">
            <div className="overall-progress">
              <span className="progress-label">Overall Progress:</span>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progressData?.overallProgress || 0}%` }}
                />
            </div>
              <span className="progress-text">{Math.round(progressData?.overallProgress || 0)}%</span>
            </div>
            </div>
          </div>
        </div>

        {/* Journal Entry Reading Modal */}
        {showEntryModal && selectedEntry && (
          <div className="journal-entry-modal-overlay" onClick={closeEntryModal}>
            <div className="journal-entry-modal" onClick={(e) => e.stopPropagation()}>
              <div className="journal-entry-header">
                <div className="entry-icon">{getEntryIcon(selectedEntry)}</div>
                <div className="entry-title-section">
                  <h3 className="entry-title">{getEntryTitle(selectedEntry)}</h3>
                  <div className="entry-meta">
                    <span className="entry-date">
                      {new Date(selectedEntry.createdAt).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </span>
                    <span className="entry-time">
                      {new Date(selectedEntry.createdAt).toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit',
                        hour12: true 
                      })}
                    </span>
                    {selectedEntry.type === 'structured' && (
                      <span className="entry-type-badge">{selectedEntry.primaryType}</span>
                    )}
                  </div>
                </div>
                <button className="close-modal-btn" onClick={closeEntryModal}>
                  <svg width="20" height="20" viewBox="0 0 20 20">
                    <path 
                      d="M15 5L5 15M5 5L15 15" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
              
              <div className="journal-entry-content">
                {selectedEntry.content ? (
                  <div className="content-text">
                    {selectedEntry.content.split('\n').map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                ) : (
                  <div className="no-content">
                    <p>This entry has no content to display.</p>
                  </div>
                )}
              </div>
              
              <div className="journal-entry-actions">
                <button 
                  className="edit-entry-btn"
                  onClick={() => {
                    if (openJournal) openJournal(selectedEntry);
                    closeEntryModal();
                  }}
                >
                  Edit Entry
                </button>
                <button 
                  className="delete-entry-btn"
                  onClick={() => {
                    if (deleteJournal) deleteJournal(selectedEntry.id);
                    closeEntryModal();
                  }}
                >
                  Delete Entry
                </button>
              </div>
            </div>
          </div>
        )}
    </div>,
    document.body
  );
};

export default CalendarModal;